import { get, post, put } from "../Service/ApiConfig";
import { ApiEndpoints, DoctorEndPoint } from "../Service/Endpoints/ApiEndPoints";

export const createDoctor = async ({ doctor }) => {
  const formData = new FormData();
  const token = localStorage.getItem("authToken");
  const keyMapping = {
    userName: "UserName",
  };

  // Append all non-image fields
  Object.entries(doctor).forEach(([key, value]) => {
    if (key === "profilePicture" || key === "signature" || key === "specialityRates") {
      // Skip, handled below
      return;
    }

    const apiKey = keyMapping[key] || key;

    if (Array.isArray(value)) {
      // For arrays, append each item
      value.forEach((item, idx) => {
        formData.append(`${apiKey}[${idx}]`, item);
      });
    } else if (value !== undefined && value !== null) {
      formData.append(apiKey, value);
    }
  });

  appendRatesToFormData(formData, doctor.specialityRates);

  // Append profile image if present
  if (doctor.profilePicture instanceof File) {
    formData.append("profileImage", doctor.profilePicture);
  }

  // Append signature image if present
  if (doctor.signature instanceof File) {
    formData.append("signatureImage", doctor.signature);
  }

  // Submit as multipart/form-data process.env.REACT_APP_BACKEND_URL
  const response = await fetch(process.env.REACT_APP_BACKEND_URL + `api/v1/doctor/doctor` , {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`, 
    },
  });

  return response.json();
};

export const getAllDoctors = async ({ page, size, searchText, language }) => {
  const response = await get(
    DoctorEndPoint.getAllDoctors(page, size, searchText, language),
  );
  return response;
};

export const getDoctorByID = async ({ id,lang }) => {
  const response = await get(DoctorEndPoint.getDoctorByID(id,lang));
  return response;
};

export const getDoctorFullDetailsByID = async (id, lang) => {
  const response = await get(DoctorEndPoint.getDoctorFullDetailsByID(id, lang));
  return response;
};

export const getLanguages = async ({ lang }) => {
  const response = await get(DoctorEndPoint.getLanguages(lang));
  return response;
};

export const getTitles = async ({ lang }) => {
  const response = await get(DoctorEndPoint.getTitles(lang));
  return response;
};

export const verifyDoctorByID = async (id) => {
  const response = await put(DoctorEndPoint.verifyDoctorByID(id));
  return response;
};

export const updateDoctor = async ({ doctor }) => {
  const formData = new FormData();
  const token = localStorage.getItem("authToken");

  const keyMapping = {
    id: "Id",
    userId: "UserId",
    firstName: "FirstName",
    lastName: "LastName",
    userName: "UserName",
    email: "Email",
    phoneNumber: "PhoneNumber",
    phoneNumberDialCode: "PhoneNumberDialCode",
    phoneNumberCountryCode: "PhoneNumberCountryCode",
    speciality: "Speciality", 
    registrationNo: "RegistrationNo",
    hourlyRate: "HourlyRate",
    onsiteHourlyRate: "OnsiteHourlyRate",
    tagline: "TagLine",
    biography: "Biography",
    experience: "Experience",
    ProfessionalLiceneseNumber: "SLMCNo",
    university: "University",
    universitySpeciality: "UniversitySpeciality",
    SpecialityNumber: "SpecialityNumber",
    treatment: "Treatment",
    title: "Title",
    languages: "Languages"
  };

  Object.entries(doctor).forEach(([key, value]) => {
    if (["profilePicture", "signature", "specialityRates", "profileImage", "signatureImage", "doctorTitle", "doctorLanguages", "doctorTreatments"].includes(key)) {
      return;
    }

    const apiKey = keyMapping[key] || key;

    if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        const val = (typeof item === 'object' && item !== null) ? (item.typeId || item.id) : item;
        if (val !== undefined && val !== null) {
          formData.append(`${apiKey}[${idx}]`, val);
        }
      });
    } else if (value !== undefined && value !== null) {
      formData.append(apiKey, value);
    }
  });

  appendRatesToFormData(formData, doctor.specialityRates);

  if (doctor.profilePicture instanceof File) formData.append("profileImage", doctor.profilePicture);
  if (doctor.signature instanceof File) formData.append("signatureImage", doctor.signature);

  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}api/v1/doctor/doctor`, {
    method: "PUT",
    body: formData,
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.json();
};


/**
 * * this method will activate or inactivate a doctor
 * @param id => get the id of the doctor
 * @param activeStatus => get the active status of the doctor
 * @return response => server response
 */
export const activationStatDoctor = async ({ id, activeStatus }) => {
  const response = await put(
    DoctorEndPoint.ActivationDoctor,
    JSON.stringify({
      id: id,
      isActivate: activeStatus,
    }),
  );

  return response;
};

export const featuredDoctorStatus = async ({ id, featuredStatus }) => {
  const response = await put(
    DoctorEndPoint.featuredDoctor,
    JSON.stringify({
      doctorId  : id,
      isFeatured: featuredStatus,
    }),
  );

  return response;
};

export const resetUserRetryCount = async (email) => {
  const response = await put(ApiEndpoints.resetUserRetryCount(email));
  return response;
}

export const searchDoctor = async (search) => {
  const response = await get(DoctorEndPoint.searchDoctors(search));
  return response;
};

export const resendResetPasswordLink = async ({ docId }) => {
  const response = await post(
    DoctorEndPoint.resendPasswordLink,
    JSON.stringify({ uuid: docId }),
  );
  return response;
};
export const blockDocByUserCode = async ({ docId, status, id }) => {
  const response = await put(
    DoctorEndPoint.blockDoctor,
    JSON.stringify({ uuid: docId, status: status, id: id }),
  );
  return response;
};



const appendRatesToFormData = (formData, specialityRates) => {
  if (!specialityRates) return;

  let index = 0;

  if (Array.isArray(specialityRates)) {
    specialityRates.forEach((rateItem) => {
      formData.append(`SpecialityRates[${index}].SpecialityId`, rateItem.specialityId);
      formData.append(`SpecialityRates[${index}].IsOnline`, rateItem.isOnline);
      formData.append(`SpecialityRates[${index}].IsPhysical`, rateItem.isPhysical);
      formData.append(`SpecialityRates[${index}].Price`, rateItem.price ?? 0);
      formData.append(`SpecialityRates[${index}].PatientPrice`, rateItem.patientPrice ?? 0); 
      index++;
    });
  } 
  else if (typeof specialityRates === 'object') {
    Object.entries(specialityRates).forEach(([specId, rates]) => {
      const hasOnlinePrice = rates.onlinePrice !== "" && rates.onlinePrice !== null && rates.onlinePrice !== undefined;
      const hasPatientOnlinePrice = rates.patientOnlinePrice !== "" && rates.patientOnlinePrice !== null && rates.patientOnlinePrice !== undefined;

      if (hasOnlinePrice || hasPatientOnlinePrice) {
        const parsed = hasOnlinePrice ? parseFloat(rates.onlinePrice) : 0;
        const patientParsed = hasPatientOnlinePrice ? parseFloat(rates.patientOnlinePrice) : 0;
        if ((!isNaN(parsed) && parsed >= 0) || (!isNaN(patientParsed) && patientParsed >= 0)) {
          formData.append(`SpecialityRates[${index}].SpecialityId`, parseInt(specId));
          formData.append(`SpecialityRates[${index}].IsOnline`, true);
          formData.append(`SpecialityRates[${index}].IsPhysical`, false);
          formData.append(`SpecialityRates[${index}].Price`, isNaN(parsed) ? 0 : parsed);
          formData.append(`SpecialityRates[${index}].PatientPrice`, isNaN(patientParsed) ? 0 : patientParsed); 
          index++;
        }
      }

      const hasPhysicalPrice = rates.physicalPrice !== "" && rates.physicalPrice !== null && rates.physicalPrice !== undefined;
      const hasPatientPhysicalPrice = rates.patientPhysicalPrice !== "" && rates.patientPhysicalPrice !== null && rates.patientPhysicalPrice !== undefined;

      if (hasPhysicalPrice || hasPatientPhysicalPrice) {
        const parsed = hasPhysicalPrice ? parseFloat(rates.physicalPrice) : 0;
        const patientParsed = hasPatientPhysicalPrice ? parseFloat(rates.patientPhysicalPrice) : 0;
        if ((!isNaN(parsed) && parsed >= 0) || (!isNaN(patientParsed) && patientParsed >= 0)) {
          formData.append(`SpecialityRates[${index}].SpecialityId`, parseInt(specId));
          formData.append(`SpecialityRates[${index}].IsOnline`, false);
          formData.append(`SpecialityRates[${index}].IsPhysical`, true);
          formData.append(`SpecialityRates[${index}].Price`, isNaN(parsed) ? 0 : parsed);
          formData.append(`SpecialityRates[${index}].PatientPrice`, isNaN(patientParsed) ? 0 : patientParsed); 
          index++;
        }
      }
    });
  }
};
